import type { ApiError } from "../types";

// ============================================================
// CONFIGURATION
// ============================================================

const BACKEND_BASE_URL =
  import.meta.env.VITE_APP_BACKEND_URL ?? "http://127.0.0.1:1337";

const AUTH_TOKEN_STORAGE_KEY = "transport_app_auth_token";

export function getBackendBaseUrl(): string {
  return BACKEND_BASE_URL.replace(/\/$/, "");
}

// ============================================================
// TOKEN MANAGEMENT
// ============================================================

export const tokenStorage = {
  getToken: (): string | null => {
    try {
      return localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  },
  setToken: (token: string): void => {
    try {
      localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
    } catch {
      console.error("[tokenStorage] Failed to persist auth token");
    }
  },
  removeToken: (): void => {
    try {
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    } catch {
      console.error("[tokenStorage] Failed to remove auth token");
    }
  },
};

// ============================================================
// REQUEST OPTIONS BUILDER
// ============================================================

function buildRequestHeaders(
  customHeaders: Record<string, string> = {},
): HeadersInit {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...customHeaders,
  };

  const authToken = tokenStorage.getToken();
  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }

  return headers;
}

// ============================================================
// ERROR PARSING
// ============================================================

async function parseErrorResponse(response: Response): Promise<ApiError> {
  let message = `HTTP ${response.status}: ${response.statusText}`;
  let details: unknown;

  try {
    const errorBody = await response.json();
    if (errorBody?.error?.message) {
      message = errorBody.error.message;
      details = errorBody.error.details;
    }
  } catch {
    // Response body is not JSON — use status text as message
  }

  return {
    status: response.status,
    message,
    details,
  };
}

function isLikelyJsonResponse(contentType: string | null, bodyText: string): boolean {
  if (contentType?.toLowerCase().includes("application/json")) {
    return true;
  }

  const trimmed = bodyText.trim();
  return trimmed.startsWith("{") || trimmed.startsWith("[");
}

// ============================================================
// CORE FETCH WRAPPER
// ============================================================

export class ApiClientError extends Error {
  public readonly apiError: ApiError;

  constructor(apiError: ApiError) {
    super(apiError.message);
    this.name = "ApiClientError";
    this.apiError = apiError;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export async function apiRequest<TResponse>(
  endpoint: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  const { method = "GET", body, headers: customHeaders, signal } = options;

  const url = `${getBackendBaseUrl()}${endpoint}`;

  const fetchOptions: RequestInit = {
    method,
    headers: buildRequestHeaders(customHeaders),
    signal,
  };

  if (body !== undefined) {
    fetchOptions.body = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(url, fetchOptions);
  } catch (networkError) {
    throw new ApiClientError({
      status: 0,
      message: "Brak połączenia z serwerem. Sprawdź połączenie internetowe.",
      details: networkError,
    });
  }

  if (!response.ok) {
    const apiError = await parseErrorResponse(response);
    throw new ApiClientError(apiError);
  }

  // Handle empty responses (204 No Content)
  if (response.status === 204) {
    return undefined as TResponse;
  }

  try {
    const responseText = await response.text();

    if (!responseText.trim()) {
      return undefined as TResponse;
    }

    const contentType = response.headers.get("content-type");
    if (!isLikelyJsonResponse(contentType, responseText)) {
      throw new ApiClientError({
        status: response.status,
        message: "Serwer zwrócił odpowiedź w nieobsługiwanym formacie.",
        details: {
          contentType,
          preview: responseText.slice(0, 200),
        },
      });
    }

    return JSON.parse(responseText) as TResponse;
  } catch (parseError) {
    if (parseError instanceof ApiClientError) {
      throw parseError;
    }

    throw new ApiClientError({
      status: response.status,
      message: "Nieprawidłowy format odpowiedzi serwera.",
    });
  }
}

// ============================================================
// QUERY STRING BUILDER
// ============================================================

type QueryValue = string | number | boolean | string[] | number[] | undefined;

export function buildStrapiQueryString(
  params: Record<string, QueryValue>,
): string {
  const queryParts: string[] = [];

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        queryParts.push(
          `${encodeURIComponent(key)}[${index}]=${encodeURIComponent(String(item))}`,
        );
      });
    } else {
      queryParts.push(
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
      );
    }
  }

  return queryParts.length > 0 ? `?${queryParts.join("&")}` : "";
}
